/**
 * La barra de confianza, en movil, como una cinta que corre de derecha a
 * izquierda sin parar.
 *
 * POR QUE HACE FALTA JAVASCRIPT. Un bucle sin costura necesita DOS copias de
 * la tira: la animacion desplaza la mitad exacta del carril y, cuando termina,
 * la segunda copia esta justo donde estaba la primera, asi que el salto al
 * reiniciar no se ve. CSS no puede duplicar nodos, y la barra se monta con la
 * seccion generica `section`, que no admite marcado propio. Aqui se envuelven
 * los cuatro datos en un carril y se clona una vez.
 *
 * ES UNA MEJORA, NO UN REQUISITO. Si el script no carga, la barra se queda como
 * estaba: cuatro filas apiladas, legibles y con su filete. No se pierde nada.
 *
 * TRES COSAS QUE NO SE HACEN, y conviene saber por que:
 *
 * 1. No corre en escritorio. Ahi los cuatro datos caben en fila y se leen de
 *    un vistazo; moverlos seria quitarle al lector el control de algo que ya
 *    podia leer quieto.
 * 2. No corre con `prefers-reduced-motion`. Un carrusel automatico es
 *    exactamente el tipo de movimiento que ese ajuste viene a apagar.
 * 3. La copia va con `aria-hidden` y sin foco. Para un lector de pantalla la
 *    barra sigue teniendo cuatro entradas, no ocho.
 *
 * La velocidad se fija en pixeles por segundo y de ahi sale la duracion, no al
 * reves: si algun dia se anade un quinto dato, la cinta corre igual de rapido
 * y solo tarda mas en dar la vuelta.
 */
(() => {
  const VELOCIDAD = 80; // px por segundo
  const CONSULTA = '(max-width: 749px)';

  /**
   * El contenedor de los datos, buscado POR LOS DATOS y no por la clase de la
   * seccion. Horizon envuelve el contenido en un `.section` y dentro en un
   * `.custom-section-content`, y esa segunda capa no siempre se llama igual:
   * buscar `.section` devolvia el envoltorio de fuera, cuyos hijos son el
   * fondo y el contenido, no los datos. Se pregunta por el primer `.nn-dato`
   * -que es lo que de verdad hay que mover- y se sube a su padre.
   */
  const zona = () => {
    const dato = document.querySelector('[id*="__confianza"] .nn-dato');
    if (!dato) return null;
    // Si ya esta montada, el padre del dato es el carril: hay que devolver el
    // contenedor de verdad, que es el que tiene la cinta colgando.
    const cinta = dato.closest('.nn-cinta');
    return cinta ? cinta.parentElement : dato.parentElement;
  };

  /** Deshace el montaje y devuelve los datos a donde estaban. */
  const desmonta = (raiz) => {
    const marco = raiz.querySelector(':scope > .nn-cinta');
    if (!marco) return;
    const carril = marco.querySelector('.nn-cinta__carril');
    for (const copia of carril.querySelectorAll('[data-nn-cinta-copia]')) copia.remove();
    while (carril.firstChild) raiz.insertBefore(carril.firstChild, marco);
    marco.remove();
    raiz.style.removeProperty('--nn-cinta-duracion');
    raiz.removeAttribute('data-nn-cinta');
  };

  const monta = (raiz) => {
    if (raiz.querySelector(':scope > .nn-cinta')) return;

    const datos = [...raiz.children].filter((n) => n.classList.contains('nn-dato'));
    if (datos.length < 2) return;

    const marco = document.createElement('div');
    marco.className = 'nn-cinta';
    const carril = document.createElement('div');
    carril.className = 'nn-cinta__carril';
    marco.append(carril);
    raiz.append(marco);
    carril.append(...datos);
    // La barra ya nacia en una fila con la caja de la cinta (CSS, «la cinta
    // antes de su script»); la marca apaga esas reglas ahora que manda el
    // carril, sin que nada cambie de alto.
    raiz.setAttribute('data-nn-cinta', '');

    // La copia: misma tira, invisible para la accesibilidad y fuera del orden
    // de tabulacion, para que nadie la lea ni caiga en ella dos veces.
    for (const dato of datos) {
      const copia = dato.cloneNode(true);
      copia.setAttribute('aria-hidden', 'true');
      copia.setAttribute('data-nn-cinta-copia', '');
      for (const enfocable of copia.querySelectorAll('a, button, [tabindex]')) {
        enfocable.setAttribute('tabindex', '-1');
      }
      carril.append(copia);
    }

    // La duracion se mide DESPUES de montar, sobre el ancho real de la mitad
    // del carril: leer `scrollWidth` fuerza el calculo de la maqueta, asi que
    // el valor ya es el bueno sin esperar a nada.
    //
    // Y SE MIDE TRES VECES, que no es paranoia: la primera vale casi siempre;
    // el `requestAnimationFrame` cubre el caso de que el navegador todavia no
    // hubiera colocado los nodos recien movidos -ahi `scrollWidth` da cero-; y
    // `fonts.ready` cubre el que de verdad muerde, que es la fuente. La cinta
    // se mide en Raleway y en la monoespaciada propias, y hasta que no llegan
    // el texto ocupa lo que ocupa en la de respaldo. Sin esa tercera medida la
    // cinta se quedaba con la duracion de reserva del CSS y corria a casi el
    // doble de lo pedido.
    const mide = () => {
      const mitad = carril.scrollWidth / 2;
      if (!mitad) return;
      raiz.style.setProperty('--nn-cinta-duracion', `${Math.round(mitad / VELOCIDAD)}s`);
    };

    mide();
    requestAnimationFrame(mide);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(mide);
  };

  const revisa = () => {
    const raiz = zona();
    if (!raiz) return;
    const movil = window.matchMedia(CONSULTA).matches;
    const quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (movil && !quieto) monta(raiz);
    else desmonta(raiz);
  };

  const arranca = () => {
    revisa();
    window.matchMedia(CONSULTA).addEventListener('change', revisa);
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', revisa);
    // El editor de temas vuelve a pintar la seccion entera al tocar cualquier
    // ajuste, y se lleva el montaje por delante.
    document.addEventListener('shopify:section:load', revisa);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arranca, { once: true });
  } else {
    arranca();
  }
})();
